import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { NativeTypes } from 'react-dnd-html5-backend';
import { DropTarget } from 'react-dnd';
import axios from 'axios';

import {
  uploadFileStart,
  uploadFile,
  uploadProgress,
  uploadFinished,
  uploadError,
  uploadRetryCount,
  uploadNetworkError,
  wholeUploadFinished,
  filesUploadHelper,
} from 'actions/UploadActions';

const fileTarget = {
  drop(props, monitor) {
    props.onDrop(monitor.getItem().files, props.upload.context.uploadLink, props.upload.context.data);
  },
};

class Upload extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      retryTimeoutPending: null,
    };

    this.isUploading = false;
    this.uploadingFile = null;
  }


  componentDidUpdate = (prevProps, prevState) => {
    // 1. Check if a current upload is complete or has an error
    if (this.isUploading && this.props.upload && this.props.upload.files) {
      for (let i = 0; i < this.props.upload.files.length; i++) {
        const file = this.props.upload.files[i];
        if (file.file === this.uploadingFile && (file.isUploaded || file.networkError || file.error !== 0)) {
          this.isUploading = false;
          this.uploadingFile = null;
          break;
        }
      }
    }

    // 2. Logic to start new uploads
    // This logic must ensure that it doesn't end up in an infinite loop
    // Check if the files array has changed (new files in the queue)
    const filesChanged = this.props.upload.files !== prevProps.upload.files;

    if (
      !this.uploadingFile && // No file upload is currently running
      !this.state.retryTimeoutPending && //No retry timeout is active
      this.props.webApp.isOnline && // Online-Status
      this.props.upload &&
      this.props.upload.files &&
      filesChanged // Only react if the files array has changed in the upload state
    ) {
      for (let i = 0; i < this.props.upload.files.length; i++) {
        const file = this.props.upload.files[i];
        if (!file.isUploading && !file.isUploaded && file.error === 0) {
          // File is in the queue and not yet started/completed/errored

          if (file.networkError) {
            // If there was a network error, wait and reboot
            this.setState({
              retryTimeoutPending: setTimeout(() => {
                this.setState({ retryTimeoutPending: null }, () => {
                  this.props.startUpload(file.file, file.uploadLink, file.args, file.retryCount);
                });
              }, file.retryCount * file.retryCount * 1000),
            });
          } else {
            // Normal upload start
            this.isUploading = true;
            this.uploadingFile = file.file;
            this.props.startUpload(file.file, file.uploadLink, file.args);
          }
          // Important: Stop here so that only one upload is started at a time
          break;
        }
      }
    }

    // Check if all uploads are finished (global isUploading status from Redux)
    // if (!this.props.upload.isUploading && prevProps.upload.isUploading) {
    //   this.props.onUploadFinished();
    // }
  };

  componentWillUnmount() {
    if (this.state.retryTimeoutPending) {
      clearTimeout(this.state.retryTimeoutPending);
    }
  }


  render() {
    const { connectDropTarget, isOver, canDrop, user } = this.props;

    const style =
      canDrop && user.logged
        ? {
            position: 'absolute',
            top: (document.body.scrollTop || document.documentElement.scrollTop) + 'px',
            left: 0,
            height: '100%',
            width: '100%',
            background: 'rgba(13,24,35,0.95)',
            zIndex: 9999,
            color: '#fff',
            textAlign: 'center',
          }
        : {
            display: 'none',
          };

    const contentWhenLoading = canDrop ? (
      <div>
        <i className="fa fa-cloud-upload" style={{ fontSize: '30em', marginTop: '30px' }} />
        <h1>Drop your files here</h1>
      </div>
    ) : (
      ''
    );

    return connectDropTarget(<div style={style}>{contentWhenLoading}</div>);
  }
}

const mapStateToProps = state => {
  return {
    webApp: state.webApp,
    user: state.login.user,
    upload: state.upload,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    onDrop: filesUploadHelper(dispatch),
    startUpload: (file, uploadLink, args, retryAttempts = 0) => {
      const retryAttemptsMax = 10;

      const source = axios.CancelToken.source();

      // progress event will dispatch an event
      const progressCallback = progressEvent => {
        return dispatch(uploadProgress(progressEvent, file));
      };
      // finished callback will trigger an event
      const finishedCallback = response => {
        return dispatch(uploadFinished(file, response));
      };
      // error callback will trigger an event
      const errorCallback = error => {
        if ((!error.response || !error.response.status) && retryAttempts < retryAttemptsMax) {
          dispatch(uploadRetryCount(file, retryAttempts + 1)); // mark the file with the information about network error
        } else {
          return dispatch(uploadError(file, error));
        }
      };
      // mark that we start an upload
      dispatch(uploadFileStart(file, retryAttempts));
      // dispatch the file upload
      dispatch(uploadFile(uploadLink, file, args, progressCallback, finishedCallback, errorCallback, source.token));

      return source;
    },
    onUploadFinished: () => {
      dispatch(wholeUploadFinished());
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    DropTarget(NativeTypes.FILE, fileTarget, (connect, monitor) => ({
      connectDropTarget: connect.dropTarget(),
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }))(Upload)
  )
);
